import BaseMap from 'components/Map/BaseMap'
import CleaningRoutesLayer from 'components/Map/CleaningRoutesLayer'
import CleaningStartFinishPoints from 'components/Map/CleaningStartFinishPoints'
import CleaningZonesLayer from 'components/Map/CleaningZonesLayer'
import Layers from 'components/Map/Layers'
import MapContainer from 'components/Map/MapContainer'
import RestrictedZonesLayer from 'components/Map/RestrictedZonesLayer'
import SingleRobotLayer from 'components/Map/SingleRobotLayer'
import TransferRoutesLayer from 'components/Map/TransferRoutesLayer'
import Widgets from 'components/Map/Widgets'
import ApriorMapToggle from 'components/Map/widgets/ApriorMapToggle'
import LayerList from 'components/Map/widgets/LayerList'
import Legend from 'components/Map/widgets/Legend/Legend'
import MapToolbar from 'components/Map/widgets/MapToolbar'
import Measurment from 'components/Map/widgets/Measurment'
import ZoomToRobot from 'components/Map/widgets/ZoomToRobot'
import { Feature as GeoJSONFeature } from 'geojson'
import useMission from 'hooks/useMission'
import useRobot from 'hooks/useRobot'
import useSetRobotTrajectory from 'hooks/useSetRobotTrajectory'
import { Coordinate } from 'ol/coordinate'
import { Extent, createEmpty, extend, isEmpty } from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import useMapStore from 'store/map'
import { DEFAULT_VIZUALIZATION_SETTINGS } from 'utils/constants'
import { ITrajectory } from 'utils/interfaces'

import toolbarStyles from 'components/Map/widgets/MapToolbar.module.css'

interface Props {
  hideControls?: boolean
}

const RobotMissionMap = ({ hideControls }: Props) => {
  const { data: robot } = useRobot()
  const { data: mission } = useMission(robot?.job?.missionId)
  const map = useMapStore(s => s.map)
  const missionId = mission?.mission.id
  const robotPositionRef = useRef<Coordinate | null>(null)
  const observedMissionIdRef = useRef<number | null>(null)
  const lastCenteredMissionIdRef = useRef<number | null>(null)
  const apriorMapExtentRef = useRef<{ missionId: number | null; extent: Extent | null }>({
    missionId: null,
    extent: null,
  })

  useSetRobotTrajectory()

  const missionExtent = useMemo((): Extent | null => {
    if (!map || !mission) return null

    const features: GeoJSONFeature[] = [
      ...mission.cleaningZones,
      ...mission.restrictZones,
      ...mission.cleaningRoutes,
      ...mission.transferRoutes,
      ...(mission.pointRoutes || []),
      ...mission.cleaningZoneStartingPoints,
      ...mission.cleaningZoneEndingPoints,
      ...(mission.startingPoint ? [mission.startingPoint] : []),
      ...(mission.finishingPoint ? [mission.finishingPoint] : []),
    ]
    const extent = createEmpty()
    const geojson = new GeoJSON()
    features.forEach(feature => {
      if (!feature.geometry) return
      const geometry = geojson.readGeometry(feature.geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: map.getView().getProjection(),
      })
      extend(extent, geometry.getExtent())
    })
    return isEmpty(extent) ? null : extent
  }, [map, mission])

  const fitExtent = useCallback(
    (extent: Extent) => map?.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 18 }),
    [map]
  )

  const getMissionOrApriorExtent = useCallback((): Extent | null => {
    const apriorMapExtent = apriorMapExtentRef.current
    return missionExtent || (apriorMapExtent.missionId === missionId ? apriorMapExtent.extent : null)
  }, [missionExtent, missionId])

  const onRobotPositionChange = useCallback(
    (position: Coordinate | null) => {
      robotPositionRef.current = position
      if (position || !missionId || lastCenteredMissionIdRef.current === missionId) return

      const extent = getMissionOrApriorExtent()
      if (!extent) return

      fitExtent(extent)
      lastCenteredMissionIdRef.current = missionId
    },
    [fitExtent, getMissionOrApriorExtent, missionId]
  )

  const onApriorMapExtentChange = useCallback(
    (extent: Extent | null) => {
      apriorMapExtentRef.current = { missionId: missionId ?? null, extent }
      if (!extent || !missionId || missionExtent || robotPositionRef.current) return
      if (lastCenteredMissionIdRef.current === missionId) return
      fitExtent(extent)
      lastCenteredMissionIdRef.current = missionId
    },
    [fitExtent, missionExtent, missionId]
  )

  useEffect(() => {
    if (observedMissionIdRef.current === missionId) return
    observedMissionIdRef.current = missionId ?? null
    lastCenteredMissionIdRef.current = null
  }, [missionId])

  useEffect(() => {
    if (!missionId || !map || robotPositionRef.current || lastCenteredMissionIdRef.current === missionId) return

    const extent = getMissionOrApriorExtent()
    if (!extent) return

    fitExtent(extent)
    lastCenteredMissionIdRef.current = missionId
  }, [fitExtent, getMissionOrApriorExtent, map, missionId])

  const centerMap = () => {
    if (!map) return
    if (robotPositionRef.current) {
      map.getView().setCenter(robotPositionRef.current)
      map.getView().setZoom(18)
      return
    }
    const extent = getMissionOrApriorExtent()
    if (extent) fitExtent(extent)
  }

  const getTransferRoutes = (): ITrajectory[] => {
    return [...(mission?.pointRoutes || []), ...(mission?.transferRoutes || [])]
  }

  const visualSettings = { ...DEFAULT_VIZUALIZATION_SETTINGS, ...mission?.visualizationSettings }

  return (
    <MapContainer hideControls={hideControls}>
      <Layers>
        <BaseMap />
        <SingleRobotLayer onPositionChange={onRobotPositionChange} />
        {mission && (
          <>
            <CleaningZonesLayer cleaningZones={mission.cleaningZones} visualSettings={visualSettings} />
            <RestrictedZonesLayer restrictedZones={mission.restrictZones} visualSettings={visualSettings} />
            <CleaningStartFinishPoints startPoint={mission.startingPoint} finishPoint={mission.finishingPoint} />
            <CleaningRoutesLayer
              cleaningRoutes={mission.cleaningRoutes}
              startingPoints={mission.cleaningZoneStartingPoints}
              endingPoints={mission.cleaningZoneEndingPoints}
              visualSettings={visualSettings}
            />
            <TransferRoutesLayer transferRoutes={getTransferRoutes()} visualSettings={visualSettings} />
          </>
        )}
      </Layers>
      <Widgets>
        {hideControls ? (
          <ApriorMapToggle
            hideButton
            missionInfo={mission?.mission ?? null}
            onExtentChange={onApriorMapExtentChange}
          />
        ) : (
          <>
            <MapToolbar>
              <ZoomToRobot embedded onClick={centerMap} />
              <Measurment embedded />
              <div className={toolbarStyles.mapGroup}>
                <LayerList embedded />
                <ApriorMapToggle
                  missionInfo={mission?.mission ?? null}
                  onExtentChange={onApriorMapExtentChange}
                />
              </div>
            </MapToolbar>
            {mission && <Legend visualSettings={visualSettings} />}
          </>
        )}
      </Widgets>
    </MapContainer>
  )
}

export default RobotMissionMap
